<template>
    <div>
        <form @submit.prevent="handleSubmit">
            <!-- Login input field -->
            <email-address-field v-model.lazy="email"/>

            <!-- Password input field -->
            <password-field
                v-model="password"
                :require-strong-password="false"/>

            <div v-if="showResetPassword">
                <h4 class="pl-3 mt-4">
                    {{ $t('PASSWORD_FIELD_HAVING_TROUBLE') }}
                    <router-link
                        :to="{ name: 'forgot-password' }"
                        class='block link'
                        v-text="$t('PASSWORD_FIELD_TRY_RESET')">
                    </router-link>
                </h4>

                <!-- Small gap -->
                <div class="clear--sm"></div>
            </div>

            <!-- Submit button -->
            <div class="text-center">
                <div v-if="showIncorrectCredentialsMessage" class="input input--invalid">
                    <p class="input__message">{{ $t('PASSWORD_FIELD_WHOOPS') }}</p>
                </div>
                <submit-button color="green" :processing="isFormProcessing">{{ $t('LOGIN_LOG_IN') }}</submit-button>
            </div>
        </form>
        <stripe-terminal-reader v-if="isCanvasserType && isUSA"/>
    </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { CANVASSER_TYPE, USER_TYPE } from '@/constants/Account';
import AuthService from '@/services/auth/AuthService';
import SubmitButton from '@/components/forms/buttons/SubmitButton';
import PasswordField from '@/components/forms/fields/PasswordField';
import EmailAddressField from '@/components/forms/fields/EmailAddressField';
import StripeTerminalReader from '../cardDetails/StripeTerminalReader';

let authService = new AuthService();

export default {
    name: 'LoginForm',
    components: {
        SubmitButton,
        PasswordField,
        EmailAddressField,
        StripeTerminalReader,
    },
    props: {
        showResetPassword: {
            default: true,
            type: Boolean,
        },
        isCanvasser: {
            default: false,
            type: Boolean,
        },
        redirectPathAfterLogin: {
            default: null,
            type: String,
        },
    },
    data() {
        return {
            email: null,
            password: null,
            incorrectCredentials: false,
            isFormProcessing: false,
        };
    },
    computed: {
        ...mapGetters({
            intendedUrl: 'auth/getIntendedUrl',
            isUserLoggedIn: 'auth/isUserLoggedIn',
            isCanvasserType: 'auth/isCanvasser',
            isUSA: 'localisation/isUSA',
        }),
        showIncorrectCredentialsMessage() {
            return this.incorrectCredentials;
        },
    },
    mounted() {
        if (this.isUserLoggedIn) this.redirectToIntendedUrl();
    },
    methods: {
        async authenticateUser() {
            this.isCanvasser ? this.canvasserLogin() : this.login();
        },

        handleLoginErrorResponse() {
            this.showErrorMessage();
            this.toggleFormProcessing();
        },

        handleLoginSuccessResponse(userType, data = {}) {
            if (data.tokens) {
                localStorage.setItem('access_token', data.tokens.access_token);
                localStorage.setItem('refresh_token', data.tokens.refresh_token);
            }

            this.$store.dispatch('auth/loginUser', userType);

            'redirect' in data ? this.redirectToGivenPage(data) : this.redirectToIntendedUrl();
        },

        redirectToGivenPage(data) {
            this.$store.commit('account/updateRescheduledDeliveryDays', data.details);
            this.$router.push({ name: data.redirect });
        },

        handleSubmit() {
            this.toggleFormProcessing();

            setTimeout(() => {
                this.authenticateUser();
            }, 500);
        },

        canvasserLogin() {
            authService.canvasserLogin(this.email, this.password)
                .then(() => this.handleLoginSuccessResponse(CANVASSER_TYPE))
                .catch(() => this.handleLoginErrorResponse());
        },

        login() {
            authService.login(this.email, this.password)
                .then((response) => this.handleLoginSuccessResponse(USER_TYPE, response.data))
                .catch(() => this.handleLoginErrorResponse());
        },

        redirectToIntendedUrl() {
            const redirectPath = this.redirectPathAfterLogin ? this.redirectPathAfterLogin : this.intendedUrl;

            setTimeout(() => this.$router.push({ name: redirectPath }), 0);
        },

        showErrorMessage() {
            this.incorrectCredentials = true;
        },

        toggleFormProcessing() {
            this.isFormProcessing = !this.isFormProcessing;
        },
    },
};
</script>
